<template>
  <button
    :class="['zg-chip', {
      'zg-chip--with-icons': icons.left || icons.right,
      'zg-chip--selected': selected,
      'zg-chip--disabled': disabled,
      'zg-chip--full-width': fullWidth,
      [`zg-chip--size-${size}`]: size
    }]"
    type="button"
  >
    <zg-icon
      v-if="icons && icons.left"
      :icon="icons.left"
      :rotate="icons.leftRotation"
    />

    <span v-if="text">
      {{ text }}
    </span>

    <zg-icon
      v-if="icons && icons.right"
      :icon="icons.right"
      :rotate="icons.rightRotation"
    />
  </button>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon'

  export default {
    name: 'ZgChip',
    components: {
      ZgIcon
    },
    props: {
      size: {
        type: String,
        required: false,
        default: 'medium',
        validator: value => ['small', 'medium', 'large'].includes(value)
      },
      icons: {
        type: Object,
        required: false,
        default: () => ({
          left: null,
          leftRotation: 0,
          right: null,
          rightRotation: 0
        })
      },
      text: {
        type: String,
        required: false,
        default: null
      },
      selected: {
        type: Boolean,
        required: false,
        default: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-chip {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    color: color(black);
    background-color: transparent;
    border: 2px solid color(primary-500);
    border-radius: radius(small);
    cursor: pointer;
    transition: all 0.3s ease;
    outline: none;

    &:hover {
      border-color: color(brand-300);
    }

    &:focus {
      border-color: color(primary-500);
      box-shadow: box-shadow(focus);
    }

    &:focus:not(:focus-visible) {
      border-color: color(primary-500);
      box-shadow: none;
    }
  }

  // ? ----------------------------------------------------------------------
  // size
  // ? ----------------------------------------------------------------------

  .zg-chip--size-small {
    @include type-body-xs;

    min-width: rem(32px);
    height: rem(32px);
    gap: rem(2px);
    padding: rem(10px);
    margin: 0;

    ::v-deep svg {
      width: rem(12px);
      height: rem(12px);
    }
  }

  .zg-chip--size-medium {
    @include type-body-s;

    min-width: rem(40px);
    height: rem(40px);
    gap: rem(4px);
    padding: rem(12px);
    margin: 0;

    ::v-deep svg {
      width: rem(16px);
      height: rem(16px);
    }
  }

  .zg-chip--size-large {
    @include type-body-m;

    min-width: rem(48px);
    height: rem(48px);
    gap: rem(6px);
    padding: rem(15px);
    margin: 0;

    ::v-deep svg {
      width: rem(18px);
      height: rem(18px);
    }
  }

  // ? ----------------------------------------------------------------------
  // icons
  // ? ----------------------------------------------------------------------

  .zg-chip--with-icons {
    ::v-deep svg {
      flex-shrink: 0;
      height: auto;
      margin: 0;
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-chip--selected {
    background-color: color(secondary-500);
    border-color: color(secondary-500);

    &:hover {
      background-color: color(brand-300);
      border-color: color(brand-300);
    }

    &:focus {
      background-color: color(secondary-500);
      border-color: color(secondary-500);
    }

    &:focus:not(:focus-visible) {
      background-color: color(secondary-500);
      border-color: color(secondary-500);
    }
  }

  .zg-chip--disabled {
    @include disabled;
  }

  // ? ----------------------------------------------------------------------
  // misc
  // ? ----------------------------------------------------------------------

  .zg-chip--full-width {
    width: 100%;
  }
</style>
